"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { analyticsTools } from "@/lib/analytics";
import type { AnalyticsConfig } from "@/lib/analytics";
import { ConsentBanner } from "./ConsentBanner";
import { CONSENT_HASH, createConsent, readConsent, writeConsent } from "./consent";
import type { ConsentChoices, ConsentRecord } from "./consent";
import { applyAnalyticsConsent, installTracking } from "./tracking";

/*
 * Deux petites sources d'état du navigateur, lues par useSyncExternalStore :
 * pendant l'hydratation React prend la valeur « serveur » (rien de lu, pas de
 * bandeau, donc aucun écart avec le HTML), puis la vraie valeur juste après.
 */

/** Choix du visiteur : `undefined` tant qu'il n'est pas lu, `null` s'il n'y en a pas de valable. */
let consentSnapshot: ConsentRecord | null | undefined;
const consentListeners = new Set<() => void>();

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getConsent(): ConsentRecord | null {
  if (consentSnapshot === undefined) {
    try {
      const store = storage();
      consentSnapshot = store ? readConsent(store) : null;
    } catch {
      consentSnapshot = null;
    }
  }
  return consentSnapshot;
}

function saveConsent(choices: ConsentChoices): void {
  try {
    const store = storage();
    consentSnapshot = store ? writeConsent(store, choices) : createConsent(choices);
  } catch {
    // navigation privée stricte : le choix vaut pour cette page seulement
    consentSnapshot = createConsent(choices);
  }
  for (const listener of consentListeners) listener();
}

function subscribeConsent(listener: () => void): () => void {
  consentListeners.add(listener);
  return () => consentListeners.delete(listener);
}

/** L'adresse vise-t-elle `#cookies` ? `history.replaceState` ne déclenche pas `hashchange` : on prévient à la main. */
const hashListeners = new Set<() => void>();

function atCookiesHash(): boolean {
  return window.location.hash === CONSENT_HASH;
}

function subscribeHash(listener: () => void): () => void {
  hashListeners.add(listener);
  window.addEventListener("hashchange", listener);
  return () => {
    hashListeners.delete(listener);
    window.removeEventListener("hashchange", listener);
  };
}

function clearCookiesHash(): void {
  if (!atCookiesHash()) return;
  // `history.state` gardé : le routeur de Next y range son état.
  window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  for (const listener of hashListeners) listener();
}

/**
 * Consentement et mesure d'audience (03/10/2026), posé une fois par le
 * layout racine, en production seulement. Lit le choix du visiteur
 * (`consent.ts`), affiche le bandeau tant qu'il n'y en a pas de valable,
 * charge ou coupe la mesure (`tracking.ts`). Le lien « Cookies »
 * (`#cookies`) rouvre le bandeau pour changer d'avis.
 */
export function CookieConsent({ analytics }: { analytics: AnalyticsConfig }) {
  const record = useSyncExternalStore(subscribeConsent, getConsent, () => undefined);
  const hashOpen = useSyncExternalStore(subscribeHash, atCookiesHash, () => false);
  const [reopened, setReopened] = useState(false);
  const returnFocus = useRef<Element | null>(null);

  // Le choix connu (ou son absence) pilote la mesure.
  useEffect(() => {
    if (record !== undefined) applyAnalyticsConsent(record ? record.analytics : null, analytics);
  }, [record, analytics]);

  // Gestes mesurés (liens, formulaires) : écoutés partout, transmis seulement après accord.
  useEffect(() => installTracking(), []);

  // Rouvert par le lien « Cookies » : le focus entre dans le bandeau, puis revient au lien.
  useEffect(() => {
    const onHashChange = (): void => {
      if (!atCookiesHash()) return;
      returnFocus.current = document.activeElement;
      setReopened(true);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const close = (): void => {
    clearCookiesHash();
    setReopened(false);
    const target = returnFocus.current;
    returnFocus.current = null;
    if (target instanceof HTMLElement && target.isConnected) target.focus();
  };

  const decide = (choices: ConsentChoices): void => {
    saveConsent(choices);
    close();
  };

  const open = record !== undefined && (record === null || hashOpen);
  if (!open) return null;
  return <ConsentBanner tools={analyticsTools(analytics)} onDecide={decide} focusOnOpen={reopened} onDismiss={record ? close : undefined} />;
}
